import type { LocalMediaBlobPort } from '../../domain/ports/LocalMediaBlobPort.ts';
import {
  LOCAL_MEDIA_IDB_STORE,
  runSharedIdbRead,
  runSharedIdbTx,
} from '@core/infrastructure/driven/indexedDb/sharedLocalIdb.ts';

function isStoredBlob(value: unknown): value is Blob {
  return (
    value instanceof Blob ||
    (typeof value === 'object' &&
      value !== null &&
      typeof (value as Blob).arrayBuffer === 'function' &&
      typeof (value as Blob).size === 'number')
  );
}

export class IndexedDbLocalMediaBlobAdapter implements LocalMediaBlobPort {
  async put(id: string, blob: Blob): Promise<void> {
    await runSharedIdbTx(
      LOCAL_MEDIA_IDB_STORE,
      'readwrite',
      (store) => {
        store.put(blob, id);
      },
      'No se pudo guardar la firma o la foto localmente.',
    );
  }

  async get(id: string): Promise<Blob | null> {
    const value = await runSharedIdbRead<unknown>(
      LOCAL_MEDIA_IDB_STORE,
      (store) => store.get(id),
      'No se pudo leer la imagen local.',
    );
    return isStoredBlob(value) ? value : null;
  }

  async delete(id: string): Promise<void> {
    await runSharedIdbTx(
      LOCAL_MEDIA_IDB_STORE,
      'readwrite',
      (store) => {
        store.delete(id);
      },
      'No se pudo eliminar la imagen local.',
    );
  }

  async toBase64(blob: Blob): Promise<string> {
    // FileReader: one decode path, no latin1 intermediate (Safari RAM).
    if (typeof FileReader !== 'undefined') {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('No se pudo codificar la imagen.'));
        reader.readAsDataURL(blob);
      });
      const comma = dataUrl.indexOf(',');
      return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    }
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }
}

export { migrateLegacyMediaDb } from './localMediaLegacyMigration.ts';
